/**
 * MobileFrame Component for LOOPINO AI Platform
 * Main container component for mobile interface
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './MobileFrame.css';
import { getViewportDimensions, addMobileDetectionListener } from '../../../utils/mobileDetection';
import { usePullToRefresh } from '../../../hooks/usePullToRefresh';
import MobileHeader from '../MobileHeader';
import MobileDock from '../MobileDock'; // RIATTIVATO
import BackgroundDots from '../BackgroundDots';
import { motion, useMotionValue, animate, useTransform } from 'framer-motion';
import { MobileSwipeBackContext } from '../MobileSwipeBackContext';

const MobileFrame = ({ 
  children, 
  currentScreen = 'posts',
  previousScreen = null,
  onScreenChange,
  isLoading = false,
  error = null,
  onRetry,
  onProfileClick,
  onBatteryClick,
  onLogoClick,
  isRegenerating = false,
  onRefresh, // Nuova prop per il refresh della pagina
  canSwipeBack = false,
  onSwipeBack,
  isTransitioning = false
}) => {
  const [viewport, setViewport] = useState(getViewportDimensions());
  const [isDebug] = useState(process.env.NODE_ENV === 'development');
  const swipeX = useMotionValue(0);
  const [isSwipeActive, setIsSwipeActive] = useState(false);
  const [isSwipeAnimating, setIsSwipeAnimating] = useState(false);
  const swipeStateRef = useRef({
    startX: 0,
    startY: 0,
    lastX: 0,
    prevSampleX: 0,
    lastTime: 0,
    prevSampleTime: 0,
    isTracking: false,
    isSwipe: false,
    isAnimating: false
  });
  const pendingResetRef = useRef(false);
  const swipeBackGuardRef = useRef(null);
  const activeScreenRef = useRef(currentScreen);
  const swipeContainerRef = useRef(null);
  const skipNextRefreshRef = useRef(false);
  const scrollPositionsRef = useRef({});
  const previewScrollTopRef = useRef(0);
  const previewScrollContainerRef = useRef(null);
  const screenNodeRefsRef = useRef({});
  const previewSnapshotRef = useRef(null);
  const [previewVersion, setPreviewVersion] = useState(0);

  // Pull-to-refresh hook
  const handleRefreshCallback = useCallback(async () => {
    if (skipNextRefreshRef.current) {
      skipNextRefreshRef.current = false;
      return;
    }

    if (onRefresh) {
      await onRefresh();
    } else {
      window.location.reload();
    }
  }, [onRefresh]);

  const {
    onTouchStart: pullTouchStart,
    onTouchMove: pullTouchMove,
    onTouchEnd: pullTouchEnd,
    containerRef,
    isPulling,
    isRefreshing,
    pullDistance,
    iconRotation,
    iconOpacity,
    iconScale,
    shouldTrigger
  } = usePullToRefresh(
    handleRefreshCallback,
    {
      threshold: 48, // Ridotto del 40% (era 80)
      maxPullDistance: 72, // Ridotto del 40% (era 120)
      resistanceRatio: 0.6,
      refreshingDuration: 1500,
      disabled: isLoading || error
    }
  );

  const EDGE_THRESHOLD_PX = 24;
  const SWIPE_ACTIVATION_DELTA = 10;
  const VELOCITY_THRESHOLD = 0.45; // circa 450px/s

  const previousScreenOpacity = useTransform(swipeX, [0, viewport.width || 1], [0, 1]);
  const previousScreenScale = useTransform(swipeX, [0, viewport.width || 1], [0.95, 1]);

  const shouldIgnoreGlobalSwipe = useCallback((event) => {
    if (!event) {
      return false;
    }

    const path = typeof event.composedPath === 'function' ? event.composedPath() : null;
    if (Array.isArray(path)) {
      return path.some((node) => {
        if (!node) {
          return false;
        }
        if (node?.dataset?.mobileSwipeBlocker === 'true') {
          return true;
        }
        return node?.classList?.contains('mobile-image-gallery-overlay');
      });
    }

    const target = event.target;
    if (target && typeof target.closest === 'function') {
      return Boolean(
        target.closest('[data-mobile-swipe-blocker="true"]') ||
        target.closest('.mobile-image-gallery-overlay')
      );
    }

    return false;
  }, []);

  // Update viewport on resize
  useEffect(() => {
    const cleanup = addMobileDetectionListener(({ viewport: newViewport }) => {
      setViewport(newViewport);
    });
    
    return cleanup;
  }, []);

  const registerScreenNode = useCallback((screenKey, node) => {
    if (!screenKey) {
      return;
    }
    if (node) {
      screenNodeRefsRef.current[screenKey] = node;
    } else {
      delete screenNodeRefsRef.current[screenKey];
    }
  }, []);

  const resetPreviewSnapshot = useCallback(() => {
    previewSnapshotRef.current = null;
    previewScrollTopRef.current = 0;
    const mount = previewScrollContainerRef.current;
    if (mount) {
      mount.innerHTML = '';
      mount.scrollTop = 0;
    }
    setPreviewVersion(prev => prev + 1);
  }, []);

  const registerSwipeBackGuard = useCallback((guardFn) => {
    swipeBackGuardRef.current = typeof guardFn === 'function' ? guardFn : null;
    return () => {
      if (swipeBackGuardRef.current === guardFn) {
        swipeBackGuardRef.current = null;
      }
    };
  }, []);

  const swipeGuardContextValue = useMemo(() => ({
    registerSwipeBackGuard
  }), [registerSwipeBackGuard]);

  // Handle screen changes (mantenuto per compatibilità futura)
  const handleScreenChange = (screen, options) => {
    console.log('[MobileFrame] Screen change requested:', screen, options);
    if (onScreenChange) {
      onScreenChange(screen, options);
    }
  };

  // Retry function for error states
  const handleRetry = () => {
    console.log('[MobileFrame] Retry requested');
    if (onRetry) {
      onRetry();
    }
  };

  useEffect(() => {
    if (activeScreenRef.current !== currentScreen) {
      activeScreenRef.current = currentScreen;
      if (pendingResetRef.current) {
        swipeX.set(0);
        pendingResetRef.current = false;
      }
      setIsSwipeActive(false);
      setIsSwipeAnimating(false);
      swipeStateRef.current.isAnimating = false;
      swipeStateRef.current.isSwipe = false;
      swipeStateRef.current.isTracking = false;
      resetPreviewSnapshot();
      skipNextRefreshRef.current = false;
      const scrollNode = containerRef.current;
      const storedScrollTop = scrollPositionsRef.current[currentScreen];
      if (scrollNode) {
        const targetScroll = typeof storedScrollTop === 'number' ? storedScrollTop : 0;
        window.requestAnimationFrame(() => {
          scrollNode.scrollTop = targetScroll;
        });
      }
    }
  }, [currentScreen, resetPreviewSnapshot, swipeX]);

  useEffect(() => {
    if (!canSwipeBack && swipeX.get() !== 0) {
      if (pendingResetRef.current) {
        // Skip snap-back while a completed swipe is waiting for screen change
        return;
      }
      animate(swipeX, 0, { type: 'spring', stiffness: 300, damping: 35 });
      setIsSwipeActive(false);
      swipeStateRef.current.isSwipe = false;
      swipeStateRef.current.isTracking = false;
    }
  }, [canSwipeBack, swipeX]);

  const handleSwipeTouchStart = useCallback((e) => {
    if (shouldIgnoreGlobalSwipe(e)) {
      return;
    }

    skipNextRefreshRef.current = false;
    pullTouchStart(e);

    const touch = e.touches[0];
    const shouldTrack = !!canSwipeBack && !isTransitioning && !isRefreshing && touch.clientX <= EDGE_THRESHOLD_PX && !isSwipeAnimating;

    if (shouldTrack) {
      if (previousScreen) {
        previewScrollTopRef.current = scrollPositionsRef.current[previousScreen] ?? 0;
        const sourceNode = screenNodeRefsRef.current[previousScreen];
        if (sourceNode) {
          const snapshot = sourceNode.cloneNode(true);
          snapshot.style.display = 'block';
          snapshot.style.visibility = 'visible';
          snapshot.removeAttribute('aria-hidden');
          snapshot.style.position = 'relative';
          previewSnapshotRef.current = snapshot;
          setPreviewVersion(prev => prev + 1);
        } else {
          previewSnapshotRef.current = null;
          setPreviewVersion(prev => prev + 1);
        }
      } else {
        previewSnapshotRef.current = null;
        setPreviewVersion(prev => prev + 1);
      }
    } else {
      resetPreviewSnapshot();
    }

    swipeStateRef.current = {
      startX: touch.clientX,
      startY: touch.clientY,
      lastX: touch.clientX,
      prevSampleX: touch.clientX,
      lastTime: e.timeStamp,
      prevSampleTime: e.timeStamp,
      isTracking: shouldTrack,
      isSwipe: false,
      isAnimating: false
    };

    if (!shouldTrack) {
      setIsSwipeActive(false);
    }
  }, [canSwipeBack, isTransitioning, isRefreshing, pullTouchStart, isSwipeAnimating, previousScreen, resetPreviewSnapshot, shouldIgnoreGlobalSwipe]);

  const handleSwipeTouchMove = useCallback((e) => {
    if (shouldIgnoreGlobalSwipe(e)) {
      return;
    }

    const touch = e.touches[0];
    let handledBySwipe = false;

    const swipeState = swipeStateRef.current;
    if (swipeState.isTracking && !swipeState.isAnimating) {
      const deltaX = touch.clientX - swipeState.startX;
      const deltaY = touch.clientY - swipeState.startY;

      if (!swipeState.isSwipe) {
        if (deltaX > SWIPE_ACTIVATION_DELTA && Math.abs(deltaX) > Math.abs(deltaY)) {
          swipeState.isSwipe = true;
          setIsSwipeActive(true);
          skipNextRefreshRef.current = true;
        } else if (Math.abs(deltaY) > Math.abs(deltaX) && Math.abs(deltaY) > 6) {
          swipeState.isTracking = false;
          resetPreviewSnapshot();
        } else if (deltaX < 0) {
          swipeState.isTracking = false;
          resetPreviewSnapshot();
        }
      }

      if (swipeState.isSwipe) {
        const offset = Math.max(0, deltaX);
        swipeX.set(offset);
        swipeState.prevSampleX = swipeState.lastX;
        swipeState.prevSampleTime = swipeState.lastTime;
        swipeState.lastX = touch.clientX;
        swipeState.lastTime = e.timeStamp;
        handledBySwipe = true;
        if (e.cancelable) {
          e.preventDefault();
        }
      }
    }

    if (!handledBySwipe) {
      pullTouchMove(e);
    }
  }, [pullTouchMove, resetPreviewSnapshot, swipeX, shouldIgnoreGlobalSwipe]);

  const finishSwipeAnimation = useCallback((target, onComplete) => {
    swipeStateRef.current.isAnimating = true;
    setIsSwipeAnimating(true);
    const controls = animate(swipeX, target, {
      type: 'spring',
      stiffness: 320,
      damping: 34
    });

    controls.then(() => {
      swipeStateRef.current.isAnimating = false;
      setIsSwipeAnimating(false);
      if (onComplete) {
        onComplete();
      }
    });
  }, [swipeX]);

  const handleSwipeTouchEnd = useCallback((e) => {
    if (shouldIgnoreGlobalSwipe(e)) {
      return;
    }

    const swipeState = swipeStateRef.current;

    if (swipeState.isSwipe) {
      const viewportWidth = viewport.width || window.innerWidth || 360;
      const traveled = Math.max(0, swipeState.lastX - swipeState.startX);
      const sampleDelta = swipeState.lastX - swipeState.prevSampleX;
      const timeDelta = Math.max(e.timeStamp - swipeState.prevSampleTime, 16);
      const velocity = sampleDelta / timeDelta;
      const distanceThreshold = Math.min(viewportWidth * 0.35, 220);
      const shouldComplete = traveled >= distanceThreshold || velocity > VELOCITY_THRESHOLD;

      pullTouchEnd();

      if (shouldComplete && typeof onSwipeBack === 'function') {
        const guardFn = swipeBackGuardRef.current;

        if (typeof guardFn === 'function') {
          finishSwipeAnimation(0, () => {
            setIsSwipeActive(false);
            swipeX.set(0);
            resetPreviewSnapshot();
          });

          Promise.resolve()
            .then(() => guardFn())
            .then((canNavigate) => {
              if (canNavigate) {
                pendingResetRef.current = true;
                onSwipeBack();
              }
            })
            .catch((error) => {
              console.error('[MobileFrame] Swipe back guard error:', error);
            });
        } else {
          finishSwipeAnimation(viewportWidth, () => {
            pendingResetRef.current = true;
            onSwipeBack();
          });
        }
      } else {
        finishSwipeAnimation(0, () => {
          setIsSwipeActive(false);
          swipeX.set(0);
          resetPreviewSnapshot();
        });
      }

      swipeStateRef.current.isSwipe = false;
      swipeStateRef.current.isTracking = false;
      return;
    }

    swipeStateRef.current.isTracking = false;
    swipeStateRef.current.isSwipe = false;
    pullTouchEnd();
    resetPreviewSnapshot();
  }, [finishSwipeAnimation, onSwipeBack, pullTouchEnd, resetPreviewSnapshot, viewport.width, shouldIgnoreGlobalSwipe]);

  const handleSwipeTouchCancel = useCallback((e) => {
    if (shouldIgnoreGlobalSwipe(e)) {
      return;
    }

    const swipeState = swipeStateRef.current;
    if (swipeState.isSwipe) {
      finishSwipeAnimation(0, () => {
        setIsSwipeActive(false);
        swipeX.set(0);
        resetPreviewSnapshot();
      });
    }
    swipeStateRef.current.isSwipe = false;
    swipeStateRef.current.isTracking = false;
    pullTouchEnd();
    resetPreviewSnapshot();
  }, [finishSwipeAnimation, pullTouchEnd, resetPreviewSnapshot, swipeX, shouldIgnoreGlobalSwipe]);

  useEffect(() => {
    const node = swipeContainerRef.current;
    if (!node) {
      return undefined;
    }

    const handleStart = (event) => {
      handleSwipeTouchStart(event);
    };

    const handleMove = (event) => {
      handleSwipeTouchMove(event);
    };

    const handleEnd = (event) => {
      handleSwipeTouchEnd(event);
    };

    const handleCancel = (event) => {
      handleSwipeTouchCancel(event);
    };

    node.addEventListener('touchstart', handleStart, { passive: true });
    node.addEventListener('touchmove', handleMove, { passive: false });
    node.addEventListener('touchend', handleEnd, { passive: false });
    node.addEventListener('touchcancel', handleCancel, { passive: false });

    return () => {
      node.removeEventListener('touchstart', handleStart);
      node.removeEventListener('touchmove', handleMove);
      node.removeEventListener('touchend', handleEnd);
      node.removeEventListener('touchcancel', handleCancel);
    };
  }, [handleSwipeTouchCancel, handleSwipeTouchEnd, handleSwipeTouchMove, handleSwipeTouchStart]);

  const shouldRenderPreview = !!previewSnapshotRef.current && (isSwipeActive || isSwipeAnimating || pendingResetRef.current);

  useEffect(() => {
    const scrollNode = containerRef.current;
    if (!scrollNode) {
      return undefined;
    }

    scrollPositionsRef.current[activeScreenRef.current] = scrollNode.scrollTop;

    const handleScroll = () => {
      scrollPositionsRef.current[activeScreenRef.current] = scrollNode.scrollTop;
    };

    scrollNode.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      scrollNode.removeEventListener('scroll', handleScroll);
    };
  }, [containerRef]);

  useEffect(() => {
    const mount = previewScrollContainerRef.current;
    if (!mount) {
      return undefined;
    }

    mount.innerHTML = '';

    if (!shouldRenderPreview || !previewSnapshotRef.current) {
      return undefined;
    }

    const snapshotNode = previewSnapshotRef.current;
    snapshotNode.style.pointerEvents = 'none';
    snapshotNode.style.display = 'block';
    snapshotNode.style.visibility = 'visible';
    snapshotNode.removeAttribute('aria-hidden');

    mount.appendChild(snapshotNode);

    const targetScroll = typeof previewScrollTopRef.current === 'number' ? previewScrollTopRef.current : 0;
    mount.scrollTop = targetScroll;

    return () => {
      if (snapshotNode.parentNode === mount) {
        mount.removeChild(snapshotNode);
      }
    };
  }, [previewVersion, shouldRenderPreview]);

  const enhancedChildren = React.Children.map(children, (child) => {
    if (React.isValidElement(child) && typeof child.type !== 'string') {
      return React.cloneElement(child, { registerScreenRef: registerScreenNode });
    }
    return child;
  });

  return (
    <MobileSwipeBackContext.Provider value={swipeGuardContextValue}>
      <div 
        className="mobile-app-frame"
        data-debug={isDebug}
        style={{
          '--viewport-width': `${viewport.width}px`,
          '--viewport-height': `${viewport.height}px`,
          '--pixel-ratio': viewport.ratio
        }}
      >
      {/* Background Dots */}
      <BackgroundDots />

      {/* Mobile Header */}
      <div className="mobile-header-container">
        <MobileHeader 
          currentScreen={currentScreen}
          onNavigate={onScreenChange}
          onProfileClick={onProfileClick}
          onBatteryClick={onBatteryClick}
          onLogoClick={onLogoClick}
          isRegenerating={isRegenerating}
        />
      </div>

      {/* Main Content Area with Pull-to-Refresh */}
      <div className="mobile-content-container">
        {/* Pull-to-Refresh Indicator - Solo icona sotto l'header */}
        <div 
          className={`mobile-pull-refresh-indicator ${isPulling ? 'pulling' : ''} ${isRefreshing ? 'refreshing' : ''} ${shouldTrigger ? 'triggered' : ''}`}
          style={{
            opacity: Math.max(0, Math.min(1, pullDistance / 40)),
            transform: `translateX(-50%) translateY(${Math.max(0, pullDistance * 0.3)}px)`
          }}
        >
          <div 
            className="mobile-refresh-icon"
            style={{
              transform: `rotate(${isRefreshing ? 'var(--refresh-rotation)' : `${iconRotation}deg`}) scale(${iconScale})`,
              opacity: iconOpacity
            }}
          >
            <svg width="24" height="24" viewBox="0 0 25 25" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M24.0839 12.552C24.0843 10.9087 23.7475 9.28286 23.0944 7.77526C22.4413 6.26766 21.4858 4.91044 20.2871 3.78773C19.0885 2.66503 17.6723 1.80078 16.1263 1.24856C14.5802 0.696338 12.9374 0.467924 11.2996 0.577475C5.61055 0.945429 0.46485 6.12478 0.11328 11.8201C-0.0564026 14.6381 0.771937 17.4258 2.4526 19.6928C4.13327 21.9598 6.55861 23.5609 9.30203 24.2145C9.51005 24.2652 9.72602 24.2744 9.9376 24.2416C10.1492 24.2087 10.3522 24.1345 10.5351 24.023C10.718 23.9116 10.8772 23.7652 11.0036 23.5922C11.1299 23.4192 11.221 23.2229 11.2716 23.0147C11.319 22.8098 11.3255 22.5976 11.2909 22.3902C11.2563 22.1829 11.1812 21.9844 11.0699 21.8061C10.9586 21.6278 10.8132 21.4732 10.6422 21.3511C10.4712 21.2291 10.2778 21.142 10.0731 21.0949C8.14488 20.6362 6.42814 19.5385 5.20152 17.9801C3.97489 16.4217 3.3104 14.494 3.3159 12.51C3.3214 10.5259 3.99657 8.60194 5.23181 7.05037C6.46705 5.49879 8.18985 4.41071 10.1206 3.96272C12.0513 3.51473 14.0766 3.73314 15.8677 4.5825C17.6588 5.43186 19.1106 6.86231 19.9874 8.64165C20.8642 10.421 21.1146 12.4448 20.6979 14.3845C20.2811 16.3242 19.2218 18.066 17.6918 19.3271V16.6195C17.6998 16.2122 17.5569 15.8163 17.2905 15.5083C17.0242 15.2003 16.6533 15.002 16.2495 14.9517C16.027 14.9298 15.8023 14.955 15.59 15.0256C15.3778 15.0961 15.1827 15.2105 15.0174 15.3613C14.8521 15.5121 14.7202 15.6959 14.6304 15.901C14.5405 16.106 14.4946 16.3276 14.4957 16.5515V22.1508C14.4957 22.7872 14.7482 23.3976 15.1978 23.8476C15.6473 24.2976 16.257 24.5505 16.8927 24.5505H22.418C22.8248 24.5585 23.2202 24.4154 23.5279 24.1488C23.8356 23.8822 24.0336 23.5109 24.0839 23.1066C24.1057 22.8838 24.0806 22.6589 24.0101 22.4464C23.9396 22.2339 23.8254 22.0387 23.6747 21.8732C23.5241 21.7077 23.3405 21.5757 23.1357 21.4857C22.9309 21.3957 22.7095 21.3498 22.4859 21.3509H20.2247C21.441 20.2313 22.412 18.8713 23.0763 17.3567C23.7406 15.8422 24.0837 14.2061 24.0839 12.552Z" fill="currentColor"/>
            </svg>
          </div>
        </div>

        {/* Loading Overlay */}
        {isLoading && (
          <div className="mobile-loading-overlay">
            <div className="mobile-loading-spinner"></div>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div className="mobile-error-container">
            <h3 className="mobile-error-title">Oops! Qualcosa è andato storto</h3>
            <p className="mobile-error-message">{error}</p>
            {onRetry && (
              <button 
                className="mobile-error-retry"
                onClick={handleRetry}
              >
                Riprova
              </button>
            )}
          </div>
        )}

        {/* Main Content */}
        {!error && (
          <div className={`mobile-screen-stack ${isSwipeActive ? 'swipe-active' : ''}`}>
            <motion.div
              className={`mobile-screen-previous${shouldRenderPreview ? ' has-content' : ''}`}
              style={{ opacity: shouldRenderPreview ? previousScreenOpacity : 0, scale: shouldRenderPreview ? previousScreenScale : 1 }}
              aria-hidden="true"
            >
              <div
                className="mobile-scrollable-content mobile-scrollable-content-preview"
                ref={previewScrollContainerRef}
              />
            </motion.div>
            <motion.div
              className="mobile-screen-current"
              style={{ x: swipeX }}
              ref={swipeContainerRef}
            >
              <div 
                className="mobile-scrollable-content"
                ref={containerRef}
                style={{
                  transform: `translateY(${Math.max(0, pullDistance * 0.5)}px)`,
                  transition: isPulling ? 'none' : 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
                }}
              >
                {enhancedChildren}
              </div>
            </motion.div>
          </div>
        )}
      </div>

      {/* Mobile Dock Navigation - RIATTIVATO */}
      <div className="mobile-dock-container">
        <MobileDock 
          currentScreen={currentScreen}
          onScreenChange={handleScreenChange}
        />
      </div>
    </div>
  </MobileSwipeBackContext.Provider>
  );
};

export default MobileFrame; 
